import Phaser from "phaser";
import i18next from "i18next";
import { ItemUI } from "../item-ui";
import { ItemSlot } from "../item-slot";
import { WindowManager } from "../window-manager";
import { COLORS } from "../colors";
import { ModernButton } from "../modern-button";
import { BaseWindow } from "./base-window";
import { playSound } from "../../utils/sound-utils";
import { SlotType, LimbType, EquipType, Item, ItemType, getItemStat } from "../../models/common";

export class PlayerWindow extends BaseWindow {
  private slots: ItemSlot[] = [];
  private slotSprites: Phaser.GameObjects.Image[] = [];
  private equipIcons: Phaser.GameObjects.Image[] = [];
  private actionButtons: Phaser.GameObjects.Container | null = null;
  private selectedSlotIndex: number | null = null;
  private windowX: number = 0;
  private windowY: number = 0;
  private windowWidth: number = 0;
  private windowHeight: number = 0;

  constructor(
    scene: Phaser.Scene,
    windowManager: WindowManager,
    uiLayer: Phaser.GameObjects.Container,
  ) {
    super(scene, windowManager);
    this.setUiLayer(uiLayer);
    this.slots = [];
    this.slotSprites = [];
    this.equipIcons = [];
  }

  private showActionButtons(
    item: Item,
    x: number,
    y: number,
    width: number,
    height: number,
    slotIdx: number,
  ) {
    // Destroy existing action buttons first
    if (this.actionButtons) {
      this.actionButtons.destroy();
      this.actionButtons = null;
    }

    this.selectedSlotIndex = slotIdx;
    const btnY: number = y + height - 8;
    const btnSpacing: number = 8;

    const buttons: ModernButton[] = [];

    // UNEQUIP button
    const unequipButton = new ModernButton({
      scene: this.scene,
      x: 0, // Will be positioned after creation
      y: btnY,
      text: i18next.t("ui:buttons.unequip"),
      style: "success",
      onClick: () => {
        // Notify server
        if (
          this.selectedSlotIndex !== null &&
          typeof window !== "undefined" &&
          (window as any).socket
        ) {
          playSound(this.scene, "pick-item-sound", null, 0.5);
          (window as any).socket.emit("unequip-item", {
            itemId: item.id,
            slotType: SlotType.EQUIP,
          });
        }
        this.slots.forEach((s) => {
          s.setSelected(false);
          s.applyBorderStyle();
        });
      },
    });
    buttons.push(unequipButton);

    // Calculate total width and position buttons
    const totalWidth = buttons.reduce((sum, btn, idx) => {
      return (
        sum + btn.buttonWidth + (idx < buttons.length - 1 ? btnSpacing : 0)
      );
    }, 0);

    let currentX: number = x + width / 2 - totalWidth / 2;
    buttons.forEach((btn: ModernButton) => {
      const btnWidth: number = btn.buttonWidth;
      btn.setX(currentX + btnWidth / 2);
      currentX += btnWidth + btnSpacing;
    });

    this.actionButtons = this.scene.add.container(0, 0);
    this.actionButtons.setDepth(100001);
    buttons.forEach((btn: ModernButton) => {
      this.actionButtons!.add(btn);
    });
    if (this.container) {
      this.container.add(this.actionButtons);
    } else {
      this.uiLayer.add(this.actionButtons);
    }
  }

  show() {
    this.hide();
    if (!this.scene) return;

    // Create base window elements (background, title, close button)
    const baseElements = this.createBaseWindow({
      title: i18next.t("ui:equipment"),
      side: "left",
    });

    // Get window bounds for positioning child elements
    const bounds = this.getWindowBounds()!;
    const { x, y, width, height } = bounds;

    // Store window bounds for use in other methods
    this.windowX = x;
    this.windowY = y;
    this.windowWidth = width;
    this.windowHeight = height;

    // LEFT COLUMN: Limb status display
    const limbNames = Object.values(LimbType);
    let limbStatus: Record<string, string> = {};
    if (window.player && window.player.data && window.player.data.limbs) {
      limbStatus = window.player.data.limbs;
    } else {
      limbNames.forEach((limb) => (limbStatus[limb] = "HEALTHY"));
    }

    let equipment: Record<string, Item> = {};
    if (window.player && window.player.data && window.player.data.equipment) {
      equipment = window.player.data.equipment;
    }

    const limbElements: Phaser.GameObjects.GameObject[] = [];
    const limbFontSize = 14;
    const limbSpacingY = 48;
    const leftColX = x + this.CONTENT_PADDING_X;
    const leftColStartY = y + this.CONTENT_PADDING_Y + 20;

    const limbToEquip: Record<LimbType, EquipType> = {
      [LimbType.Head]: EquipType.Head,
      [LimbType.Chest]: EquipType.Chest,
      [LimbType.Legs]: EquipType.Legs,
      [LimbType.Feet]: EquipType.Feet,
    };

    limbNames.forEach((limb, idx) => {
      const status = limbStatus[limb] || "HEALTHY";
      const equipType = limbToEquip[limb];
      let maxArmor = 0;
      let currentArmor = 0;
      const limbItem = equipType && equipment ? equipment[equipType] : undefined;
      if (limbItem) {
        maxArmor = getItemStat(limbItem, "armor");
        currentArmor =
          limbItem.currentArmor !== undefined ? limbItem.currentArmor : maxArmor;
      }

      const yPos = leftColStartY + idx * limbSpacingY;

      // Limb name header
      const limbNameText = this.scene.add
        .text(leftColX, yPos, limb.toUpperCase(), {
          fontSize: "14px",
          color: "#ffffff",
          fontFamily: "Anton",
          fontStyle: "bold",
        })
        .setOrigin(0, 0);
      limbNameText.setShadow(2, 2, "#000000", 0.7);
      limbNameText.setScrollFactor(0);
      limbElements.push(limbNameText);

      // Underline
      const underlineGraphics = this.scene.add.graphics();
      underlineGraphics.lineStyle(2, 0x1a3550, 1);
      underlineGraphics.beginPath();
      underlineGraphics.moveTo(leftColX, yPos + 20);
      underlineGraphics.lineTo(leftColX + 120, yPos + 20);
      underlineGraphics.strokePath();
      underlineGraphics.setScrollFactor(0);
      limbElements.push(underlineGraphics);

      // Armor info with icon (right aligned to underline)
      const armorIcon = this.scene.add.image(
        leftColX + 120,
        yPos + 2,
        "armor-icon",
      );
      armorIcon.setDisplaySize(16, 16);
      armorIcon.setOrigin(1, 0);
      armorIcon.setScrollFactor(0);
      limbElements.push(armorIcon);

      const armorRatio = maxArmor > 0 ? currentArmor / maxArmor : 1;
      const armorColor =
        maxArmor === 0
          ? "#888888"
          : armorRatio >= 1
            ? COLORS.GREEN
            : armorRatio >= 0.5
              ? COLORS.YELLOW
              : COLORS.RED;
      const armorLabel =
        maxArmor > 0 ? `${currentArmor} / ${maxArmor}` : "—";
      const armorText = this.scene.add
        .text(leftColX + 120 - 24, yPos, armorLabel, {
          fontSize: "14px",
          color: armorColor,
          fontFamily: "Anton",
        })
        .setOrigin(1, 0);
      armorText.setShadow(2, 2, "#000000", 0.7);
      armorText.setScrollFactor(0);
      limbElements.push(armorText);

      // Status with color coding
      const statusColor = status === "BLEEDING" ? COLORS.RED : COLORS.GREEN;
      const statusText = this.scene.add
        .text(leftColX, yPos + 24, `✓ ${status}`, {
          fontSize: "14px",
          color: statusColor,
          fontFamily: "Anton",
          fontStyle: "bold",
        })
        .setOrigin(0, 0);
      statusText.setShadow(2, 2, "#000000", 0.7);
      statusText.setScrollFactor(0);
      limbElements.push(statusText);
    });

    // RIGHT COLUMN: Equipped items box
    const rightColX = x + width / 2 + 16;
    const rightColStartY = y + this.CONTENT_PADDING_Y + 20;
    const boxWidth = width / 2 - 32;
    const boxHeight = height - this.CONTENT_PADDING_Y - 40;

    // Item slots grid (2x3) inside the equipped items box
    this.slots = [];
    this.slotSprites = [];
    this.equipIcons = [];
    const gridCols = 2;
    const gridRows = 3;
    const tempSlot = new ItemSlot(0, 0);
    const slotSize = tempSlot.size;
    const slotPaddingX = 12;
    const slotPaddingY = 12;
    const itemGridStartX = rightColX;
    const itemGridStartY = rightColStartY;

    const itemInfo = this.scene.add
      .text(x + width - this.CONTENT_PADDING_X, y + 7, "", {
        fontSize: "16px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(1, 0);
    itemInfo.setShadow(2, 2, "#000000", 0.7);
    itemInfo.setScrollFactor(0);
    limbElements.push(itemInfo);
    const itemTypeInfo = this.scene.add
      .text(x + width - this.CONTENT_PADDING_X, y + 27, "", {
        fontSize: "12px",
        color: "#ffffff",
        fontFamily: "Anton",
        align: "right",
      })
      .setOrigin(1, 0);
    itemTypeInfo.setScrollFactor(0);
    limbElements.push(itemTypeInfo);

    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < gridCols; col++) {
        const slotIdx = row * gridCols + col;
        const slotX = itemGridStartX + col * (slotSize + slotPaddingX);
        const slotY = itemGridStartY + row * (slotSize + slotPaddingY);
        const equipType = Object.values(EquipType)[slotIdx];

        const item: Item | null = equipment[equipType] || null;

        const hasItem: boolean = !!item;
        const selected: boolean = false;
        const itemSlot: ItemSlot = new ItemSlot(slotX, slotY, hasItem);
        itemSlot.setSelected(selected);
        itemSlot.setEquipType(equipType);
        itemSlot.createRectangle(this.scene);

        if (itemSlot.rect) {
          itemSlot.rect.on("pointerdown", () => {
            this.selectedSlotIndex = slotIdx;
            this.slots.forEach((s, idx) => {
              s.setSelected(idx === slotIdx);
              s.applyBorderStyle();
            });
            let equipmentNow: Partial<Record<EquipType, Item>> =
              window.player &&
              window.player.data &&
              window.player.data.equipment
                ? window.player.data.equipment
                : {};
            const itemNow: Item | null = equipmentNow[equipType] || null;

            if (itemNow) {
              playSound(this.scene, "menu-click-sound", null, 0.5);
              this.showActionButtons(itemNow, this.windowX, this.windowY, this.windowWidth, this.windowHeight, slotIdx);
              if (itemInfo && itemNow.definition.name) {
                itemInfo.setText(itemNow.definition.name);
                const def = itemNow.definition;
                const typeStr = def.type === ItemType.Equipment && def.equipType ? def.equipType : def.type;
                itemTypeInfo.setText(typeStr);
              }
            } else {
              if (this.actionButtons) {
                this.actionButtons.destroy();
                this.actionButtons = null;
              }
              if (itemInfo) {
                itemInfo.setText("");
                itemTypeInfo.setText("");
              }
            }
          });
        }

        if (item) {
          const sprite = ItemUI.render(
            this.scene,
            item.definition,
            slotX,
            slotY,
            slotSize,
          );
          if (sprite) {
            sprite.setOrigin(0.5);
            sprite.setScrollFactor(0);
            this.slotSprites.push(sprite);
          }
        } else {
          itemSlot.setEquipType(equipType);
          const iconKey: string = `equip-${equipType.toLowerCase()}`;
          const iconImg = this.scene.add.image(
            slotX + slotSize / 2,
            slotY + slotSize / 2,
            iconKey,
          );
          iconImg.setDisplaySize(slotSize * 0.6, slotSize * 0.6);
          iconImg.setOrigin(0.5);
          iconImg.setScrollFactor(0);
          this.equipIcons.push(iconImg);
        }
        this.slots.push(itemSlot);
      }
    }

    // Container
    this.container = this.scene.add.container(0, 0, [
      ...baseElements,
      ...limbElements,
      ...this.slots.map((s) => s.rect).filter((r) => r !== null),
      ...this.slotSprites,
      ...this.equipIcons,
    ]);
    this.container.setDepth(100000);
    this.visible = true;

    this.addContainerToParent(this.container);
  }

  hide() {
    // Destroy action buttons if they exist
    if (this.actionButtons) {
      this.actionButtons.destroy();
      this.actionButtons = null;
    }
    // Clean up slots and sprites
    this.slots = [];
    this.slotSprites = [];
    this.equipIcons = [];
    this.selectedSlotIndex = null;
    super.hide();
  }
}
